import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { uploadAPI, productsAPI } from '../../services/api';
import { Crop } from 'lucide-react';
import ProductImageCropper from './ProductImageCropper';
import '../admin/AdminComponents.css';

const ProductForm = ({ product, onClose }) => {
  const { products, categories, textures, addProduct, updateProduct } = useAdmin();
  const [formData, setFormData] = useState({
    name: '',
    category: 'all',
    categories: [],
    price: 0,
    discountedPrice: '',
    description: '',
    additionalInfo: '',
    textures: [],
    featured: false,
    newArrival: false,
    bundleEnabled: false,
    bundleQuantity: 3,
    bundleDiscountPercent: 0,
    hidden: false,
    images: {},
  });
  const [modsByTexture, setModsByTexture] = useState({});
  const [pendingModsByTexture, setPendingModsByTexture] = useState({});
  const [loadingMods, setLoadingMods] = useState(false);
  const [imageEditor, setImageEditor] = useState(null);

  useEffect(() => {
    if (product) {
      const productCategories = product.categories || (product.category ? [product.category] : []);
      setFormData({
        name: product.name || '',
        category: product.category || 'all',
        categories: productCategories,
        price: parseFloat(product.price) || 0,
        discountedPrice: product.discountedPrice ?? product.discounted_price ?? '',
        description: product.description || '',
        additionalInfo: product.additionalInfo || product.additional_info || '',
        textures: product.textures || [],
        featured: product.featured || false,
        newArrival: Boolean(product.newArrival ?? product.new_arrival),
        bundleEnabled: Boolean(product.bundleEnabled ?? product.bundle_enabled),
        bundleQuantity: Number(product.bundleQuantity ?? product.bundle_quantity) || 3,
        bundleDiscountPercent: parseFloat(product.bundleDiscountPercent ?? product.bundle_discount_percent) || 0,
        hidden: Boolean(product.hidden),
        images: product.images || {},
      });

      if (product.id) {
        loadModsForProduct(product.id);
      }
    }
  }, [product]);

  const loadModsForProduct = async (productId) => {
    setLoadingMods(true);
    try {
      const response = await productsAPI.getMods(productId);
      if (response.success && response.data) {
        const grouped = {};
        response.data.forEach(mod => {
          if (!grouped[mod.texture]) {
            grouped[mod.texture] = [];
          }
          grouped[mod.texture].push(mod);
        });
        setModsByTexture(grouped);
      }
    } catch (error) {
      console.error('Error loading mods:', error);
    } finally {
      setLoadingMods(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextureToggle = (texture) => {
    setFormData(prev => ({
      ...prev,
      textures: prev.textures.includes(texture)
        ? prev.textures.filter(t => t !== texture)
        : [...prev.textures, texture]
    }));
  };

  const handleImageChange = (texture, value) => {
    setFormData(prev => ({
      ...prev,
      images: {
        ...prev.images,
        [texture]: value
      }
    }));
  };

  const handleImageUpload = async (texture, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setImageEditor({ texture, source: file });
  };

  const applyProductImage = async (file) => {
    const texture = imageEditor.texture;
    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success && response.data) {
        const imageUrl = response.data.url || response.data.path;
        if (!imageUrl) throw new Error('The upload did not return an image URL. Please try again.');
        handleImageChange(texture, imageUrl);
      } else {
        throw new Error(response.message || 'Error uploading image');
      }
      setImageEditor(null);
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleModUpload = async (files, texture) => {
    if (!files || files.length === 0 || !texture) return;

    const fileArray = Array.from(files);

    for (const file of fileArray) {
      if (file.size > 100 * 1024 * 1024) {
        alert(`${file.name}: Mod file size must be less than 100MB`);
        return;
      }
    }

    if (product && product.id) {
      try {
        const uploadPromises = fileArray.map(file => uploadAPI.uploadMod(file));
        const responses = await Promise.all(uploadPromises);

        const uploadedPaths = [];
        for (let i = 0; i < responses.length; i++) {
          const response = responses[i];
          if (response.success && response.data) {
            uploadedPaths.push(response.data.path || response.data.url);
          } else {
            alert(`${fileArray[i].name}: ${response.message || 'Error uploading mod file'}`);
          }
        }

        if (uploadedPaths.length > 0) {
          const response = await productsAPI.addMods(product.id, texture, uploadedPaths);
          if (response.success) {
            const modsResponse = await productsAPI.getMods(product.id, texture);
            if (modsResponse.success && modsResponse.data) {
              setModsByTexture(prev => ({
                ...prev,
                [texture]: modsResponse.data
              }));
            }
          } else {
            alert(response.message || 'Error saving mod files');
          }
        }
      } catch (error) {
        console.error('Mod upload error:', error);
        alert(error.message || 'Error uploading mod file');
      }
    } else {
      setPendingModsByTexture(prev => ({
        ...prev,
        [texture]: [...(prev[texture] || []), ...fileArray]
      }));
      alert(`Mod files will be uploaded after the product is created. ${fileArray.length} file(s) queued for ${texture} texture.`);
    }
  };

  const handleRemoveModFile = async (modId, texture) => {
    if (!product || !product.id) {
      alert('Cannot remove: Product ID not found');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this mod file?')) {
      return;
    }

    try {
      const response = await productsAPI.deleteMod(product.id, modId);
      if (response.success) {
        setModsByTexture(prev => ({
          ...prev,
          [texture]: (prev[texture] || []).filter(mod => mod.id !== modId)
        }));
      } else {
        alert(response.message || 'Error removing mod file');
      }
    } catch (error) {
      console.error('Error removing mod file:', error);
      alert(error.message || 'Error removing mod file');
    }
  };

  const handleDownloadModFile = async (modId, filePath) => {
    if (!product || !product.id) {
      alert('Cannot download: Product ID not found');
      return;
    }

    try {
      const blob = await productsAPI.downloadMod(product.id, modId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filePath.split('/').pop() || 'mod-file');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert(error.message || 'Error downloading file. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (imageEditor) return;

    if (!formData.name || !formData.category || formData.price <= 0) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.discountedPrice !== '' && parseFloat(formData.discountedPrice) <= 0) {
      alert('Discounted price must be greater than 0');
      return;
    }

    if (formData.bundleEnabled) {
      const requiredTextures = Number.parseInt(formData.bundleQuantity, 10) || 3;
      const discountPercent = parseFloat(formData.bundleDiscountPercent) || 0;
      if (requiredTextures < 2 || requiredTextures > 20) {
        alert('Texture bundle quantity must be between 2 and 20');
        return;
      }
      if (formData.textures.length < requiredTextures) {
        alert(`Select at least ${requiredTextures} textures before enabling this bundle`);
        return;
      }
      if (discountPercent <= 0 || discountPercent > 100) {
        alert('Texture bundle discount must be greater than 0 and no more than 100');
        return;
      }
    }

    const images = {
      default: formData.images.default || (formData.textures.length > 0 ? formData.images[formData.textures[0]] : '') || '',
      ...formData.images
    };

    const productData = {
      ...formData,
      images,
      categories: formData.categories.length > 0 ? formData.categories : [formData.category],
    };

    try {
      let updatedProduct;
      if (product) {
        updatedProduct = await updateProduct(product.id, productData);
      } else {
        updatedProduct = await addProduct(productData);

        if (updatedProduct && updatedProduct.id && Object.keys(pendingModsByTexture).length > 0) {
          for (const [texture, files] of Object.entries(pendingModsByTexture)) {
            if (files && files.length > 0) {
              try {
                const uploadPromises = files.map(file => uploadAPI.uploadMod(file));
                const responses = await Promise.all(uploadPromises);

                const uploadedPaths = [];
                for (let i = 0; i < responses.length; i++) {
                  const response = responses[i];
                  if (response.success && response.data) {
                    uploadedPaths.push(response.data.path || response.data.url);
                  } else {
                    console.error(`${files[i].name}: ${response.message || 'Error uploading mod file'}`);
                  }
                }

                if (uploadedPaths.length > 0) {
                  await productsAPI.addMods(updatedProduct.id, texture, uploadedPaths);
                }
              } catch (error) {
                console.error(`Error uploading mods for texture ${texture}:`, error);
              }
            }
          }
          setPendingModsByTexture({});
        }
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      onClose();
    } catch (error) {
      alert(error.message || 'Failed to save product');
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={() => { if (!imageEditor) onClose(); }}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3 className="admin-modal-title">
            {product ? 'Edit Product' : 'Add Product'}
          </h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal-body">
          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="admin-form-group">
              <label className="admin-form-label">Name *</label>
              <input
                type="text"
                name="name"
                className="admin-form-input"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Primary Category *</label>
              <select
                name="category"
                className="admin-form-select"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Additional Categories (Optional)</label>
              <div className="admin-texture-selector">
                {categories.map(cat => (
                  <label key={cat.id} className="admin-texture-option">
                    <input
                      type="checkbox"
                      checked={formData.categories.includes(cat.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            categories: [...prev.categories, cat.id]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            categories: prev.categories.filter(c => c !== cat.id)
                          }));
                        }
                      }}
                    />
                    <span>{cat.name}</span>
                  </label>
                ))}
              </div>
              <span className="admin-form-hint">Select multiple categories to make this product appear in multiple sections</span>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Price *</label>
              <input
                type="number"
                name="price"
                className="admin-form-input"
                value={formData.price}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Discounted Price (Optional)</label>
              <input
                type="number"
                name="discountedPrice"
                className="admin-form-input"
                value={formData.discountedPrice}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="Leave empty for no discount"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Description</label>
              <textarea
                name="description"
                className="admin-form-textarea"
                value={formData.description}
                onChange={handleChange}
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Additional Information (Optional)</label>
              <input
                type="text"
                name="additionalInfo"
                className="admin-form-input"
                value={formData.additionalInfo}
                onChange={handleChange}
                maxLength={300}
                placeholder="Designed by [Name]"
              />
              <span className="admin-form-hint">Shown beneath the product name in the shop.</span>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Textures (Optional)</label>
              <div className="admin-texture-selector">
                {Array.isArray(textures) && textures.length > 0 ? (
                  textures.map(texture => (
                    <label key={texture.id} className="admin-texture-option">
                      <input
                        type="checkbox"
                        checked={formData.textures.includes(texture.id)}
                        onChange={() => handleTextureToggle(texture.id)}
                      />
                      <span
                        className="admin-texture-color"
                        style={{ backgroundColor: texture.color }}
                      />
                      <span>{texture.name}</span>
                    </label>
                  ))
                ) : (
                  <p style={{ color: 'var(--text-secondary)' }}>No textures available. Create textures first.</p>
                )}
              </div>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Images</label>
              <div className="admin-image-inputs">
                <div className="admin-image-input-group">
                  <label>Default</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.images.default || ''}
                    onChange={(e) => handleImageChange('default', e.target.value)}
                    placeholder="Image URL or upload"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    className="admin-form-input-file"
                    onChange={(e) => {
                      handleImageUpload('default', e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                  {formData.images.default && (
                    <button type="button" className="admin-button product-image-edit" onClick={() => setImageEditor({ texture: 'default', source: formData.images.default })}>
                      <Crop size={16} /> Edit Crop
                    </button>
                  )}
                  {formData.images.default && (
                    <img
                      src={formData.images.default}
                      alt="Default preview"
                      className="admin-image-preview-small"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
                {formData.textures.map(textureId => {
                  const texture = Array.isArray(textures) ? textures.find(t => t.id === textureId) : null;
                  if (!texture) return null;
                  return (
                  <div key={textureId} className="admin-image-input-group">
                    <label>{texture.name}</label>
                    <input
                      type="text"
                      className="admin-form-input"
                      value={formData.images[textureId] || ''}
                      onChange={(e) => handleImageChange(textureId, e.target.value)}
                      placeholder="Image URL or upload"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      className="admin-form-input-file"
                      onChange={(e) => {
                        handleImageUpload(textureId, e.target.files[0]);
                        e.target.value = '';
                      }}
                    />
                    {formData.images[textureId] && (
                      <button type="button" className="admin-button product-image-edit" onClick={() => setImageEditor({ texture: textureId, source: formData.images[textureId] })}>
                        <Crop size={16} /> Edit Crop
                      </button>
                    )}
                    {formData.images[textureId] && (
                      <img
                        src={formData.images[textureId]}
                        alt={`${texture.name} preview`}
                        className="admin-image-preview-small"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="admin-form-group">
              <label className="admin-checkbox-label">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                />
                Featured
              </label>
            </div>
            <div className="admin-form-group">
              <label className="admin-checkbox-label">
                <input
                  type="checkbox"
                  name="newArrival"
                  checked={formData.newArrival}
                  onChange={handleChange}
                />
                New
              </label>
            </div>
            <div className="admin-form-group">
              <label className="admin-checkbox-label">
                <input
                  type="checkbox"
                  name="bundleEnabled"
                  checked={formData.bundleEnabled}
                  onChange={handleChange}
                />
                Enable Texture Bundle
              </label>
              <span className="admin-form-hint">Discounts a set when the customer chooses different textures of this product.</span>
            </div>
            {formData.bundleEnabled && (
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Different Textures Required</label>
                  <input
                    type="number"
                    name="bundleQuantity"
                    className="admin-form-input"
                    value={formData.bundleQuantity}
                    onChange={handleChange}
                    min="2"
                    max="20"
                    step="1"
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Bundle Discount (%)</label>
                  <input
                    type="number"
                    name="bundleDiscountPercent"
                    className="admin-form-input"
                    value={formData.bundleDiscountPercent}
                    onChange={handleChange}
                    min="0.01"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>
            )}
            <div className="admin-form-group">
              <label className="admin-checkbox-label">
                <input
                  type="checkbox"
                  name="hidden"
                  checked={formData.hidden}
                  onChange={handleChange}
                />
                Hidden (admin-only; removed from public shop/cart)
              </label>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Mod Files (Per Texture)</label>
              <small className="admin-form-hint" style={{ display: 'block', marginBottom: '1rem' }}>
                Upload mod files for each texture. Mods will be included in the archive based on the selected texture.
              </small>
              {formData.textures && formData.textures.length > 0 ? (
                formData.textures.map((textureId) => {
                  const textureData = Array.isArray(textures) ? textures.find(t => t.id === textureId) : null;
                  const textureName = textureData?.name || textureId;
                  const modsForTexture = modsByTexture[textureId] || [];
                  return (
                    <div key={textureId} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid var(--border)', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <span
                          className="admin-texture-color"
                          style={{
                            backgroundColor: textureData?.color || '#ccc',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            display: 'inline-block'
                          }}
                        />
                        <label style={{ fontWeight: 600 }}>{textureName} Texture Mods</label>
                        {product && product.id && (
                          <input
                            type="file"
                            accept=".zip,.rar,.7z,.ytd,.ydd,.dds,.png,.jpg,.jpeg,.gif,.webp,.txt,.json"
                            className="admin-form-input-file"
                            multiple
                            onChange={(e) => {
                              handleModUpload(e.target.files, textureId);
                              e.target.value = '';
                            }}
                            style={{ marginLeft: 'auto', fontSize: '0.85rem' }}
                            disabled={loadingMods}
                          />
                        )}
                      </div>
                      {product && product.id && modsForTexture.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                          {modsForTexture.map((mod) => (
                            <div
                              key={mod.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.5rem',
                                backgroundColor: 'var(--bg-tertiary)',
                                border: '1px solid var(--border)',
                                borderRadius: '4px',
                                gap: '0.5rem'
                              }}
                            >
                              <span style={{ fontSize: '0.9rem', wordBreak: 'break-all', flex: 1 }}>
                                {mod.mod_file_path.split('/').pop() || mod.mod_file_path}
                              </span>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  type="button"
                                  onClick={() => handleDownloadModFile(mod.id, mod.mod_file_path)}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: 'var(--accent)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                  title="Download file"
                                >
                                  Download
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveModFile(mod.id, textureId)}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: 'var(--error)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                  title="Remove file"
                                >
                                  Remove
                                </button>

                              </div>

                            </div>

                          ))}
                        </div>

                      )}

                      {product && product.id && modsForTexture.length === 0 && (
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                          No mod files uploaded for this texture yet.
                        </p>

                      )}

                      {(!product || !product.id) && (
                        <>
                          <input
                            type="file"
                            accept=".zip,.rar,.7z,.ytd,.ydd,.dds,.png,.jpg,.jpeg,.gif,.webp,.txt,.json"
                            className="admin-form-input-file"
                            multiple
                            onChange={(e) => {
                              handleModUpload(e.target.files, textureId);
                              e.target.value = '';
                            }}
                            style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}
                            disabled={loadingMods}
                          />

                          {pendingModsByTexture[textureId] && pendingModsByTexture[textureId].length > 0 && (
                            <p style={{ fontSize: '0.85rem', color: 'var(--accent)', fontStyle: 'italic', marginTop: '0.5rem' }}>
                              {pendingModsByTexture[textureId].length} file(s) queued - will upload after product is created
                            </p>

                          )}
                        </>

                      )}
                    </div>

                  );
                })
              ) : (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  Select at least one texture to upload mod files.
                </p>

              )}
            </div>


            <div className="admin-form-actions">
              <button type="button" className="admin-button" onClick={onClose}>
                Cancel
              </button>

              <button type="submit" className="admin-button primary">
                {product ? 'Update' : 'Create'}
              </button>

            </div>

          </form>

        </div>

      </div>
      {imageEditor && <ProductImageCropper
        source={imageEditor.source}
        label={imageEditor.texture === 'default' ? 'Default image' : `${textures.find(texture => texture.id === imageEditor.texture)?.name || imageEditor.texture} image`}
        onCancel={() => setImageEditor(null)}
        onApply={applyProductImage}
      />}
    </div>

  );
};

export default ProductForm;

